/**
 * Losing a company mid-session: any call can answer `403
 * COMPANY_ACCESS_REVOKED {companyId}`, and an address can name a company the
 * person no longer holds. The query client reports the first here; the
 * company layout reports the second; the signed-in layout listens, re-reads the
 * permissions, and takes the person home only when the company is gone.
 */
import { isApiError } from "@linkhr/api-client/core";
import { problemArgs } from "../api/problem";

type Listener = (companyId: string) => void;

const listeners = new Set<Listener>();

export function companyAccessLost(companyId: string): void {
  listeners.forEach((listener) => listener(companyId));
}

export function onCompanyAccessLost(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** `QueryCache` / `MutationCache` `onError`: pass a revoked company on, ignore the rest. */
export function reportCompanyAccessLost(
  error: unknown,
  query?: { meta?: Record<string, unknown> | undefined },
): void {
  // Open items answer this code for one side the reader can't see, not for a lost company.
  if (query?.meta?.["sideScoped"] === true) return;
  if (!isApiError(error) || error.errorCode !== "COMPANY_ACCESS_REVOKED") return;
  const companyId = problemArgs(error).companyId;
  // Without an id there is nothing to dedupe on; the company layout catches the address case.
  if (typeof companyId === "string") companyAccessLost(companyId);
}
